export const CAPTURE_METHOD_OPTIONS = [
  "Angling",
  "Electrofishing",
  "Fyke Net",
  "Gillnet",
  "Seine",
];

export const SAMPLING_METHOD_OPTIONS = [
  "Fishing",
  "Electrofishing",
  "Fyke Net",
  "Gillnet",
  "Seine",
];

export function normalizeCaptureMethod(value) {
  if (!value) return "";
  const trimmed = String(value).trim();
  const lower = trimmed.toLowerCase();
  const map = {
    trap: "Fyke Net",
    "net-gill": "Gillnet",
    "net-seine": "Seine",
    "fyke net": "Fyke Net",
    gillnet: "Gillnet",
    seine: "Seine",
    angling: "Angling",
    electrofishing: "Electrofishing",
    "e-fishing": "Electrofishing",
    efishing: "Electrofishing",
    "electro shocking": "Electrofishing",
    electroshocking: "Electrofishing",
    "electro-shocking": "Electrofishing",
    net: "Seine",
  };
  if (CAPTURE_METHOD_OPTIONS.includes(trimmed)) return trimmed;
  return map[lower] || trimmed;
}

export function normalizeSamplingMethod(value) {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (SAMPLING_METHOD_OPTIONS.includes(trimmed)) return trimmed;
  const lower = trimmed.toLowerCase();
  const map = {
    fishing: "Fishing",
    angling: "Fishing",
    electrofishing: "Electrofishing",
    "e-fishing": "Electrofishing",
    efishing: "Electrofishing",
    "electro shocking": "Electrofishing",
    electroshocking: "Electrofishing",
    "electro-shocking": "Electrofishing",
    "fyke net": "Fyke Net",
    trap: "Fyke Net",
    gillnet: "Gillnet",
    "gill net": "Gillnet",
    "net-gill": "Gillnet",
    seine: "Seine",
    "seine net": "Seine",
    "net-seine": "Seine",
    net: "Seine",
  };
  return map[lower] || trimmed;
}

export function isAnglingSamplingMethod(method) {
  const lower = String(method || "").trim().toLowerCase();
  return lower === "fishing" || lower === "angling" || lower === "castnet";
}

export function effortTypeForSamplingMethod(method) {
  const lower = String(method || "").trim().toLowerCase();
  if (lower === "electrofishing" || lower.includes("electro")) return "electrofishing_area";
  if (lower === "fyke net" || lower === "trap") return "fyke_net";
  if (lower === "gillnet" || lower.includes("gill")) return "gillnet";
  if (lower === "seine" || lower.includes("seine") || lower === "net") return "seine_haul";
  return null;
}

export function requiresEffortUnits(method) {
  return effortTypeForSamplingMethod(method) != null;
}

/** How fish assignment column behaves on Fish Samples entry. */
export function getFishAssignmentMode(samplingMethod) {
  if (isAnglingSamplingMethod(samplingMethod)) return "angler";
  if (requiresEffortUnits(samplingMethod)) return "effort_unit";
  return "capture_method";
}

/** Column header for the contextual assignment field on Fish Samples entry. */
export function getFishAssignmentColumnLabel(samplingMethod) {
  const effortType = effortTypeForSamplingMethod(samplingMethod);
  if (effortType === "electrofishing_area") return "Area";
  if (effortType === "seine_haul") return "Haul";
  if (effortType === "fyke_net" || effortType === "gillnet") return "Net";
  if (isAnglingSamplingMethod(samplingMethod)) return "Angler";
  return "Capture Method";
}

export function mapSamplingMethodToCaptureMethod(samplingMethod) {
  const lower = String(samplingMethod || "").trim().toLowerCase();
  if (lower.includes("electro")) return "Electrofishing";
  if (lower.includes("fyke") || lower === "trap") return "Fyke Net";
  if (lower.includes("gill")) return "Gillnet";
  if (lower.includes("seine") || lower === "net") return "Seine";
  return "Angling";
}

export function hmsToDecimalHours(timeStr) {
  if (!timeStr) return 0;
  const parts = parseHmsParts(timeStr);
  if (!parts) {
    const legacy = String(timeStr).trim().split(":").map((p) => parseInt(p, 10));
    if (legacy.some((n) => Number.isNaN(n))) return 0;
    const [h = 0, m = 0, s = 0] = legacy;
    return h + m / 60 + s / 3600;
  }
  return parts.h + parts.m / 60 + parts.s / 3600;
}

/** Parse HH:MM, HH:MM:SS, or 12-hour strings into { h, m, s }. */
export function parseHmsParts(timeStr) {
  if (timeStr == null || timeStr === "") return null;
  if (timeStr instanceof Date && !Number.isNaN(timeStr.getTime())) {
    return {
      h: timeStr.getHours(),
      m: timeStr.getMinutes(),
      s: timeStr.getSeconds(),
    };
  }
  const trimmed = String(timeStr).trim();
  const match = trimmed.match(
    /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(AM|PM)?$/i
  );
  if (!match) return null;

  let h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  const s = match[3] != null ? parseInt(match[3], 10) : 0;
  const ampm = match[4];

  if (Number.isNaN(h) || Number.isNaN(m) || Number.isNaN(s)) return null;
  if (m < 0 || m > 59 || s < 0 || s > 59) return null;

  if (ampm) {
    const upper = ampm.toUpperCase();
    if (upper === "PM" && h < 12) h += 12;
    if (upper === "AM" && h === 12) h = 0;
  }

  return { h, m, s };
}

export function isValidHmsInput(value) {
  return parseHmsParts(value) != null;
}

export function formatHmsParts({ h, m, s }) {
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Normalize user input to HH:MM:SS for display. */
export function normalizeHmsInput(value) {
  const parts = parseHmsParts(value);
  if (!parts) return String(value || "").trim();
  return formatHmsParts(parts);
}

export function formatClockTimeParts({ h, m }) {
  const hour24 = ((Number(h) % 24) + 24) % 24;
  const period = hour24 >= 12 ? "PM" : "AM";
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

export function normalizeClockTimeInput(value) {
  const parts = parseHmsParts(value);
  if (!parts) return String(value || "").trim();
  return formatClockTimeParts(parts);
}

export function isValidClockTimeInput(value) {
  const parts = parseHmsParts(value);
  if (!parts) return false;
  return parts.h >= 0 && parts.h <= 23;
}

/** Stored H:M:S → value for `<input type="time">` / display. */
export function toHtmlTimeValue(timeStr) {
  const parts = parseHmsParts(timeStr);
  if (!parts) return "";
  return formatHmsParts(parts);
}

/** `<input type="time">` value → stored H:M:S. */
export function fromHtmlTimeValue(timeInputValue) {
  const parts = parseHmsParts(timeInputValue);
  if (!parts) return "";
  return `${parts.h}:${String(parts.m).padStart(2, "0")}:${String(parts.s).padStart(2, "0")}`;
}

export function toClockTimeValue(timeStr) {
  const parts = parseHmsParts(timeStr);
  if (!parts) return "";
  return formatClockTimeParts(parts);
}

export function fromClockTimeValue(timeInputValue) {
  const parts = parseHmsParts(timeInputValue);
  if (!parts) return "";
  return `${parts.h}:${String(parts.m).padStart(2, "0")}:00`;
}

export function durationHoursFromRange(startStr, stopStr) {
  if (!startStr || !stopStr) return 0;
  const start = hmsToDecimalHours(startStr);
  const stop = hmsToDecimalHours(stopStr);
  if (stop >= start) return stop - start;
  return 24 - start + stop;
}

export function formatHMS(decimalHours) {
  if (!decimalHours || decimalHours <= 0) return "0:00:00";
  const totalSec = Math.round(decimalHours * 3600);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function parseToDecimalHours(startTime, stopTime) {
  return durationHoursFromRange(startTime, stopTime);
}

/** Decimal hours → "Xh Ym" (or N/A). */
export function formatDurationHoursAsHm(decimalHours) {
  if (!Number.isFinite(decimalHours) || decimalHours <= 0) return "N/A";
  const totalMin = Math.round(decimalHours * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
}

/**
 * Time the crew was on site for a sampling session.
 * Prefers session TimeFishingStarted/Stopped; if missing/unparseable, sums
 * electrofishing area hours, then earliest→latest effort-unit clock span.
 */
export function resolveSessionOnSiteTimes(detail) {
  const sessionStart = detail?.TimeFishingStarted;
  const sessionStop = detail?.TimeFishingStopped;
  const sessionHours = durationHoursFromRange(sessionStart, sessionStop);
  if (sessionHours > 0) {
    return {
      start: sessionStart,
      stop: sessionStop,
      hours: sessionHours,
      source: "session",
    };
  }

  const units = detail?.EffortUnits || [];

  // Electrofishing area timers are elapsed (often reset per area) — sum them.
  let sumHours = 0;
  for (const unit of units) {
    const effortType = unit?.EffortType ?? unit?.effortType;
    if (effortType && effortType !== "electrofishing_area") continue;
    const start = unit?.TimeStarted ?? unit?.timeStarted;
    const stop = unit?.TimeStopped ?? unit?.timeStopped;
    sumHours += durationHoursFromRange(start, stop);
  }
  if (sumHours > 0) {
    return {
      start: sessionStart || null,
      stop: sessionStop || null,
      hours: sumHours,
      source: "effort_sum",
    };
  }

  const timed = [];
  for (const unit of units) {
    const start = unit?.TimeStarted ?? unit?.timeStarted;
    const stop = unit?.TimeStopped ?? unit?.timeStopped;
    if (!parseHmsParts(start) || !parseHmsParts(stop)) continue;
    const startH = hmsToDecimalHours(start);
    let stopH = hmsToDecimalHours(stop);
    if (stopH < startH) stopH += 24;
    timed.push({ start, stop, startH, stopH });
  }

  if (timed.length) {
    timed.sort((a, b) => a.startH - b.startH);
    const earliest = timed[0];
    const latest = timed.reduce((best, row) =>
      row.stopH >= best.stopH ? row : best
    );
    const hours = durationHoursFromRange(earliest.start, latest.stop);
    if (hours > 0) {
      return {
        start: earliest.start,
        stop: latest.stop,
        hours,
        source: "effort_span",
      };
    }
  }

  return {
    start: sessionStart || null,
    stop: sessionStop || null,
    hours: 0,
    source: "none",
  };
}

/** Total Time Fishing/Sampling label for Table View / exports. */
export function formatSessionOnSiteDuration(detail) {
  return formatDurationHoursAsHm(resolveSessionOnSiteTimes(detail).hours);
}

function countFishForUnit(samplingData, effortUnitId, speciesName = null) {
  return (samplingData || []).filter((fish) => {
    const unitId = fish.EffortUnitID ?? fish.effortUnitID;
    if (String(unitId) !== String(effortUnitId)) return false;
    if (speciesName) {
      return (fish.FishTypeName || "Unknown") === speciesName;
    }
    return true;
  }).length;
}

/** Fish count for one effort unit (area, net, or haul). */
export function countFishForEffortUnit(samplingData, effortUnitId) {
  if (effortUnitId == null || effortUnitId === "") return 0;
  return countFishForUnit(samplingData, effortUnitId);
}

/** Per-angler fish counts from individual fish rows (read-only summary). */
export function buildAnglerFishSummary(samplingData, pondAnglers = []) {
  const counts = new Map();
  let unassigned = 0;

  for (const fish of samplingData || []) {
    const staffId = fish.AnglerStaffID ?? fish.anglerStaffID;
    if (staffId == null || staffId === "") {
      unassigned += 1;
      continue;
    }
    const key = String(staffId);
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  const rows = (pondAnglers || []).map((angler) => ({
    staffId: angler.staffId,
    label: angler.fullName,
    fishCount: counts.get(String(angler.staffId)) || 0,
  }));

  for (const [staffId, fishCount] of counts) {
    if (!rows.some((row) => String(row.staffId) === staffId)) {
      const name =
        (samplingData || []).find(
          (fish) => String(fish.AnglerStaffID ?? fish.anglerStaffID) === staffId
        )?.AnglerStaffName || `Angler ${staffId}`;
      rows.push({ staffId, label: name, fishCount });
    }
  }

  rows.sort((a, b) => a.label.localeCompare(b.label));

  return {
    rows,
    unassigned,
    total: (samplingData || []).length,
  };
}

export function computeElectrofishingCpue(effortUnits, samplingData, speciesName = null) {
  const areas = (effortUnits || [])
    .filter((u) => u.EffortType === "electrofishing_area")
    .sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0));

  const rows = areas.map((area) => {
    const hours = durationHoursFromRange(area.TimeStarted, area.TimeStopped);
    const fishCount = countFishForUnit(samplingData, area.id, speciesName);
    const cpue = hours > 0 ? (fishCount / hours).toFixed(2) : "-";
    return {
      label: area.Label,
      timeDisplay: formatHMS(hours),
      hours,
      fishCount,
      cpue,
    };
  });

  const totalHours = rows.reduce((sum, r) => sum + r.hours, 0);
  const totalFish = rows.reduce((sum, r) => sum + r.fishCount, 0);
  const avgCpue = totalHours > 0 ? (totalFish / totalHours).toFixed(2) : "-";

  return { rows, totalHours, totalFish, avgCpue };
}

export function computeNetCpue(effortUnits, samplingData, effortType, speciesName = null) {
  const nets = (effortUnits || [])
    .filter((u) => u.EffortType === effortType)
    .sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0));

  const isSeine = effortType === "seine_haul";

  const rows = nets.map((net) => {
    const fishCount = countFishForUnit(samplingData, net.id, speciesName);
    const effort = isSeine ? 1 : (parseInt(net.NetNights, 10) || 0);
    const cpue = effort > 0 ? (fishCount / effort).toFixed(1) : "-";
    return {
      label: net.Label,
      effort,
      effortLabel: isSeine ? null : effort,
      fishCount,
      cpue,
    };
  });

  const totalEffort = isSeine
    ? rows.length
    : rows.reduce((sum, r) => sum + r.effort, 0);
  const totalFish = rows.reduce((sum, r) => sum + r.fishCount, 0);
  const avgCpue = totalEffort > 0 ? (totalFish / totalEffort).toFixed(1) : "-";

  return { rows, totalEffort, totalFish, avgCpue, isSeine };
}

export function getCaptureMethodCounts(samplingData, speciesName) {
  const counts = {
    angling: 0,
    seine: 0,
    gillnet: 0,
    fykeNet: 0,
    electrofishing: 0,
  };
  (samplingData || [])
    .filter((fish) => (fish?.FishTypeName || "Unknown") === speciesName)
    .forEach((fish) => {
      const method = normalizeCaptureMethod(
        fish?.CaptureMethod || fish?.captureMethod || ""
      ).toLowerCase();
      if (method === "angling") counts.angling += 1;
      else if (method === "seine") counts.seine += 1;
      else if (method === "gillnet") counts.gillnet += 1;
      else if (method === "fyke net") counts.fykeNet += 1;
      else if (method === "electrofishing") counts.electrofishing += 1;
    });
  return counts;
}

/** Marker fish name in downloaded template example row (skipped on import). */
export const EXCEL_EXAMPLE_ROW_MARKER = "(Example row - delete me)";

export function getExcelImportColumnSpec(assignmentMode) {
  const base = [
    {
      key: "FishTypeName",
      header: "FishTypeName",
      required: true,
      description: "Required. Copy from the Fish Types List sheet.",
    },
    {
      key: "Length",
      header: "Length",
      required: true,
      description: "Required. Positive number in the session length unit.",
    },
    {
      key: "Weight",
      header: "Weight",
      required: true,
      description: "Required. Positive number in the session weight unit.",
    },
    {
      key: "TagNumber",
      header: "TagNumber",
      required: false,
      description: "Optional tag number.",
    },
  ];

  if (assignmentMode === "effort_unit") {
    return [
      ...base,
      {
        key: "EffortUnit",
        header: "EffortUnit",
        required: true,
        description:
          "Required. Area, Net, or Haul label from the Effort Units sheet. CaptureMethod is set automatically.",
      },
      {
        key: "Status",
        header: "Status",
        required: false,
        description: "Optional: Culled or Released.",
      },
    ];
  }

  if (assignmentMode === "angler") {
    return [
      ...base,
      {
        key: "Angler",
        header: "Angler",
        required: true,
        description: "Required. Angler name from the Anglers sheet. CaptureMethod is set to Angling.",
      },
      {
        key: "Status",
        header: "Status",
        required: false,
        description: "Optional: Culled or Released.",
      },
    ];
  }

  return [
    ...base,
    {
      key: "CaptureMethod",
      header: "CaptureMethod",
      required: false,
      description: "Optional. See Capture Method Options sheet.",
    },
    {
      key: "Status",
      header: "Status",
      required: false,
      description: "Optional: Culled or Released.",
    },
  ];
}

function normalizeExcelColumnKey(key) {
  return String(key || "").replace(/\s+/g, "").toLowerCase();
}

/** True for blank rows or the template example row. */
export function isSkippableImportRow(row) {
  if (!row || typeof row !== "object") return true;
  const fishName = String(row.FishTypeName ?? row["Fish Type"] ?? "").trim();
  if (!fishName && row.Length == null && row.Weight == null) return true;
  if (fishName.toLowerCase() === EXCEL_EXAMPLE_ROW_MARKER.toLowerCase()) return true;
  if (fishName.toLowerCase() === "required") return true;
  return false;
}

export function filterImportRows(rows) {
  return (rows || []).filter((row) => !isSkippableImportRow(row));
}

function rowHasColumn(row, ...aliases) {
  const keys = new Set(Object.keys(row || {}).map((k) => normalizeExcelColumnKey(k)));
  return aliases.some((alias) => keys.has(normalizeExcelColumnKey(alias)));
}

/** Validate that uploaded file has required columns for this session's assignment mode. */
export function validateExcelRequiredColumns(rows, assignmentMode) {
  const importRows = filterImportRows(rows);
  if (!importRows.length) {
    return {
      ok: false,
      missing: [],
      message:
        "No fish rows found on the Sampling Data sheet. Add your fish rows or delete the example row and try again.",
    };
  }

  const firstRow = importRows[0];
  const missing = [];
  const spec = getExcelImportColumnSpec(assignmentMode);

  for (const col of spec) {
    if (!col.required) continue;
    if (col.key === "EffortUnit") {
      if (
        !rowHasColumn(
          firstRow,
          "EffortUnit",
          "Effort Unit",
          "Area",
          "Net",
          "Haul"
        )
      ) {
        missing.push(col.header);
      }
      continue;
    }
    if (col.key === "Angler") {
      if (!rowHasColumn(firstRow, "Angler", "Angler Name", "AnglerStaff")) {
        missing.push(col.header);
      }
      continue;
    }
    if (!rowHasColumn(firstRow, col.header, col.key)) {
      missing.push(col.header);
    }
  }

  if (missing.length) {
    return {
      ok: false,
      missing,
      message: `Missing required column(s): ${missing.join(", ")}. Download the latest template for this session.`,
    };
  }

  return { ok: true, missing: [], message: "" };
}

/** Build a FishSampling create row; omit null optional FK fields for DB compatibility. */
export function buildFishSamplePayload(fields) {
  const sample = { ...fields };

  if (sample.FisherID != null && sample.FisherID !== "") {
    sample.FisherID = parseInt(sample.FisherID, 10);
  } else {
    delete sample.FisherID;
  }

  if (sample.EffortUnitID != null && sample.EffortUnitID !== "") {
    sample.EffortUnitID = parseInt(sample.EffortUnitID, 10);
  } else {
    delete sample.EffortUnitID;
  }

  if (sample.AnglerStaffID != null && sample.AnglerStaffID !== "") {
    sample.AnglerStaffID = parseInt(sample.AnglerStaffID, 10);
  } else {
    delete sample.AnglerStaffID;
  }

  if (sample.Status === "") {
    delete sample.Status;
  }

  return sample;
}

/** Prefer fresh API effort units; fall back to list payload from GetPondDetails. */
export function resolveEffortUnits(detail, apiUnits) {
  const fromApi = Array.isArray(apiUnits) ? apiUnits : [];
  const fromDetail = Array.isArray(detail?.EffortUnits) ? detail.EffortUnits : [];
  return fromApi.length > 0 ? fromApi : fromDetail;
}

/** Run async mapper over items with a fixed concurrency limit. */
export async function mapWithConcurrency(items, fn, limit = 4) {
  const list = items || [];
  const results = [];
  for (let i = 0; i < list.length; i += limit) {
    const batch = list.slice(i, i + limit);
    const batchResults = await Promise.all(batch.map(fn));
    results.push(...batchResults);
  }
  return results;
}
